import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Calendar,
  MapPin,
  Heart,
  Luggage,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './HeroSection.css';

// ─── Hero Slides (still used for text content, but background is video) ──
const heroSlides = [
  {
    id: 1,
    title: 'Your Dream\nHoneymoon Awaits.',
    subtitle: 'Curated experiences for the first chapter of\nyour happily ever after.',
  },
  {
    id: 2,
    title: 'Discover Luxury\nOverwater Haven.',
    subtitle: 'Private infinity pools, candlelight dining, and\nendless turquoise horizons.',
  },
  {
    id: 3,
    title: 'Unforgettable\nCoastal Getaways.',
    subtitle: 'Tailor-made itineraries designed exclusively\nfor romantic couples.',
  },
];

const HeroSection = () => {
  const navigate = useNavigate();

  // ─── Background Text Slider ───────────────────────────────────────
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // ─── Destinations Search ──────────────────────────────────────────
  const [destinations, setDestinations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [travelDate, setTravelDate] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await fetch('https://admintest.jayrajgroup.org/api/destinations', {
          headers: {
            'X-API-TOKEN': '50cd682741a5119f4fc86830fe99960aaad2fb302377586ef753599def7af8b5',
          },
        });
        const result = await response.json();
        if (result.status && result.data) {
          setDestinations(result.data);
        }
      } catch (error) {
        console.error('Error fetching destinations:', error);
      }
    };
    fetchDestinations();
  }, []);

  const filteredDestinations = destinations.filter((d) =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectDestination = (dest) => {
    setSelectedDestination(dest);
    setSearchTerm(dest.name);
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedDestination(null);
    setShowSuggestions(false);
  };

  const handleSearchSubmit = () => {
    const location = selectedDestination?.name || searchTerm.trim() || '';
    const params = new URLSearchParams();
    if (location) params.set('location', encodeURIComponent(location));
    if (travelDate) params.set('date', travelDate);
    navigate(`/honeymoon/packages?${params.toString()}`);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <section className="hb-hero-container">
      {/* ─── AI Video Background ────────────────────────────────── */}
      <div className="hb-video-bg">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="hb-video-element"
          poster="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1920&q=80"
        >
          <source
            src="https://cdn.pixabay.com/video/2022/01/26/105697-675193527_large.mp4"
            type="video/mp4"
          />
          {/* Fallback image if video fails */}
          <img
            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1920&q=80"
            alt="Honeymoon"
          />
        </video>
        <div className="hb-video-overlay"></div>
      </div>

      {/* ─── Floating Decorations ────────────────────────────────── */}
      <div className="hb-floating-doodle doodle-left">
        <Heart className="doodle-heart-left" size={24} />
      </div>
      <div className="hb-floating-doodle doodle-right">
        <svg viewBox="0 0 160 80" className="flight-path">
          <path d="M 10 70 Q 70 10 150 40" fill="none" stroke="#5c7882" strokeWidth="2" strokeDasharray="4,4" />
        </svg>
        <Luggage className="doodle-luggage" size={24} />
        <Heart className="doodle-heart-right" size={20} />
      </div>

      {/* ─── Main Content ────────────────────────────────────────── */}
      <div className="hb-hero-content">
        {/* Text content that changes (with key to animate on slide) */}
        <div className="hb-text-content" key={heroSlides[heroIndex].id}>
          <h1 className="hb-title">
            {heroSlides[heroIndex].title.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i === 0 && <br />}
              </React.Fragment>
            ))}
          </h1>
          <p className="hb-subtitle">
            {heroSlides[heroIndex].subtitle.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i === 0 && <br />}
              </React.Fragment>
            ))}
          </p>
        </div>

        {/* ─── Search Bar (entrance animation only on initial mount) ── */}
        <div className="hb-search-container" ref={searchRef}>
          <div className="hb-search-pill">
            <div className="hb-search-field hb-destination-field">
              <MapPin size={18} className="hb-field-icon" />
              <input
                type="text"
                placeholder="Where to?"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSuggestions(true);
                  if (!e.target.value) setSelectedDestination(null);
                }}
                onFocus={() => setShowSuggestions(true)}
                className="hb-destination-input"
                aria-label="Destination"
              />
              {searchTerm && (
                <button className="hb-clear-btn" onClick={handleClearSearch}>
                  <X size={16} />
                </button>
              )}
              {showSuggestions && filteredDestinations.length > 0 && (
                <ul className="hb-suggestions">
                  {filteredDestinations.map((dest) => (
                    <li key={dest.id} onClick={() => handleSelectDestination(dest)}>
                      <MapPin size={14} />
                      <span>{dest.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="hb-field-divider" />

            <div className="hb-search-field hb-date-field">
              <Calendar size={18} className="hb-field-icon" />
              <input
                type="date"
                value={travelDate}
                onChange={(e) => setTravelDate(e.target.value)}
                className="hb-date-input"
                min={new Date().toISOString().split('T')[0]}
                aria-label="Travel date"
              />
            </div>

            <button className="hb-search-btn" onClick={handleSearchSubmit}>
              <Search size={18} />
              <span>Explore Packages</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;